import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { UserPlus, ChevronDown, Star, Briefcase, Link2, Unlink } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/auth-context";
import { format } from "date-fns";

export type PulseParticipant = {
  email: string;
  displayName: string | null;
  kind: 'from' | 'to' | 'cc';
  status: 'linked' | 'known_unlinked' | 'unknown' | 'internal';
  contactId: string | null;
  contactName: string | null;
  role: string | null;
  unconfirmedParticipantId: number | null;
};

export const ROLE_OPTIONS: Array<{ value: string; label: string; hint?: string }> = [
  { value: 'c_level', label: 'C-Level', hint: 'Geschäftsleitung, Abteilungsleitung, o.ä.' },
  { value: 'main_contact', label: 'Main project contact', hint: 'Major player — suggested on top for project emails' },
  { value: 'other', label: 'Other' },
];

export const ROLE_LABELS: Record<string, string> = {
  c_level: 'C-Level',
  main_contact: 'Main contact',
  other: 'Other',
};

import { splitParticipantsForDisplay } from "@/lib/participant-overflow";

interface EmailPulseParticipantsProps {
  pulseId: number;
  projectId: string;
  direction?: 'incoming' | 'outgoing';
  /** The project's client (company) id, used as the default company selection. */
  defaultClientId?: string | null;
}

export function EmailPulseParticipants({ pulseId, projectId, defaultClientId, direction = 'incoming' }: EmailPulseParticipantsProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [approveTarget, setApproveTarget] = useState<PulseParticipant | null>(null);
  const [approveForm, setApproveForm] = useState({ firstName: '', lastName: '', clientId: '', noCompany: false, role: 'other' });
  const [companySearch, setCompanySearch] = useState('');
  // Multi-email contacts: attach the address to an existing contact instead of creating a new one.
  const [attachContact, setAttachContact] = useState<{ id: string; name: string; email: string } | null>(null);
  const [contactSearch, setContactSearch] = useState('');
  const [fastAddTarget, setFastAddTarget] = useState<PulseParticipant | null>(null);
  const [fastAddRole, setFastAddRole] = useState('other');
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const { data, isLoading, isError, refetch } = useQuery<{ participants: PulseParticipant[]; linked?: boolean }>({
    queryKey: ['/api/pulses', pulseId, 'participants'],
    queryFn: async () => {
      const res = await apiRequest('GET', `/api/pulses/${pulseId}/participants`);
      if (!res.ok) throw new Error('Failed to fetch participants');
      return res.json();
    },
    enabled: !!pulseId,
    staleTime: 30000,
  });

  const { data: clients = [] } = useQuery<Array<{ id: string; companyName: string; isActive?: boolean }>>({
    queryKey: ['/api/clients'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/clients');
      if (!res.ok) throw new Error('Failed to fetch clients');
      return res.json();
    },
    enabled: !!approveTarget,
  });

  // Suggestions: which existing contact does this unknown address probably belong to?
  const { data: suggestionData } = useQuery<{ suggestions: Array<{ contactId: string; firstName: string; lastName: string; email?: string; reasons: string[] }> }>({
    queryKey: ['/api/contact-persons/suggest', approveTarget?.email, approveTarget?.displayName],
    queryFn: async () => {
      const params = new URLSearchParams({ email: approveTarget!.email });
      if (approveTarget!.displayName) params.set('name', approveTarget!.displayName);
      const res = await apiRequest('GET', `/api/contact-persons/suggest?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch suggestions');
      return res.json();
    },
    enabled: !!approveTarget,
    staleTime: 60000,
  });

  // All contacts for the manual "attach to existing contact" search.
  const { data: allContacts = [] } = useQuery<Array<{ id: string; firstName: string; lastName: string; email: string }>>({
    queryKey: ['/api/contact-persons'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/contact-persons');
      if (!res.ok) throw new Error('Failed to fetch contacts');
      return res.json();
    },
    enabled: !!approveTarget,
    staleTime: 60000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['/api/pulses', pulseId, 'participants'] });
    queryClient.invalidateQueries({ queryKey: ['/api/projects', projectId, 'contacts'] });
    queryClient.invalidateQueries({ queryKey: ['/api/projects', projectId, 'unconfirmed-participants'] });
    queryClient.invalidateQueries({ queryKey: ['/api/contact-persons'] });
  };

  const fastAddMutation = useMutation({
    mutationFn: async ({ contactId, role }: { contactId: string; role: string }) => {
      const res = await apiRequest('POST', `/api/projects/${projectId}/contacts/${contactId}/link`, { role });
      if (!res.ok) throw new Error('Failed to add contact to project');
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      setFastAddTarget(null);
      toast({ title: "Added to project" });
    },
    onError: () => toast({ title: "Failed to add contact to project", variant: "destructive" }),
  });

  const approveMutation = useMutation({
    mutationFn: async (body: { email: string; firstName?: string; lastName?: string; clientId?: string | null; role: string; attachToContactId?: string }) => {
      const res = await apiRequest('POST', `/api/projects/${projectId}/participants/approve`, body);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to approve participant');
      }
      return res.json();
    },
    onSuccess: (result: any) => {
      invalidate();
      setApproveTarget(null);
      setAttachContact(null);
      toast({ title: result?.attached ? "Email address added to existing contact" : "Participant approved and added to project" });
    },
    onError: (e: Error) => toast({ title: e.message, variant: "destructive" }),
  });

  const unlinkMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('DELETE', `/api/pulses/${pulseId}/email-link`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to unlink email');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/pulses', pulseId, 'participants'] });
      queryClient.invalidateQueries({ queryKey: ['/api/email-pulses/unlinked'] });
      toast({ title: "Source email unlinked" });
    },
    onError: (e: Error) => toast({ title: e.message, variant: "destructive" }),
  });

  const participants = data?.participants || [];
  const isUnlinked = !isLoading && !!data && data.linked === false;

  if (direction === 'outgoing' && isError) {
    return <div className="text-xs text-gray-400" role="alert">
      Could not load email recipients.{' '}
      <button type="button" className="underline" onClick={() => void refetch()}>Retry</button>
    </div>;
  }

  // Task #48: old email pulses without a source-email backlink — admins can link one manually.
  if (participants.length === 0) {
    // Outgoing messages have a fixed sent-message identity; never offer the
    // incoming-only manual source-link workflow for them.
    if (direction === 'outgoing') return (
      <div className="text-xs text-gray-400" data-testid={`email-participants-${pulseId}`}>
        {isLoading ? 'Loading email recipients…' : 'Recipient details are unavailable for this sent email.'}
      </div>
    );
    if (isLoading || !isUnlinked || !isAdmin) return null;
    return (
      <div data-testid={`email-participants-${pulseId}`}>
        <button
          type="button"
          onClick={() => setLinkDialogOpen(true)}
          data-testid={`button-link-source-email-${pulseId}`}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-600 px-2 py-0.5 text-[11px] text-gray-400 hover:text-gray-200 hover:border-gray-400"
        >
          <Link2 className="w-3 h-3" />
          Link source email…
        </button>
        <LinkSourceEmailDialog
          pulseId={pulseId}
          projectId={projectId}
          open={linkDialogOpen}
          onOpenChange={setLinkDialogOpen}
        />
      </div>
    );
  }

  // From / To / CC each get their own row; empty rows are hidden.
  const rows = ([
    { key: 'from', label: 'From', items: participants.filter(p => p.kind === 'from') },
    { key: 'to', label: 'To', items: participants.filter(p => p.kind === 'to') },
    { key: 'cc', label: 'CC', items: participants.filter(p => p.kind === 'cc') },
  ] as const).filter(r => r.items.length > 0);

  const openApproveDialog = (p: PulseParticipant) => {
    const name = (p.displayName || '').trim();
    const parts = name.split(/\s+/);
    setApproveForm({
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ') || '',
      clientId: defaultClientId || '',
      noCompany: false,
      role: 'other',
    });
    setCompanySearch('');
    setAttachContact(null);
    setContactSearch('');
    setApproveTarget(p);
  };

  const handleChipClick = (p: PulseParticipant) => {
    if (p.status === 'known_unlinked') {
      setFastAddRole('other');
      setFastAddTarget(p);
    } else if (p.status === 'unknown') {
      openApproveDialog(p);
    }
  };

  const renderChip = (p: PulseParticipant) => {
    const label = p.contactName || p.displayName || p.email;
    const clickable = p.status === 'known_unlinked' || p.status === 'unknown';
    return (
      <button
        key={p.email}
        type="button"
        disabled={!clickable}
        onClick={() => handleChipClick(p)}
        title={p.email}
        data-testid={`participant-chip-${p.email}`}
        className={cn(
          "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] max-w-[220px]",
          p.kind === 'from' && "font-semibold",
          p.status === 'linked' && "border-gray-600 bg-gray-800/60 text-gray-200 cursor-default",
          p.status === 'known_unlinked' && "border-blue-600/60 bg-blue-900/20 text-blue-300 hover:bg-blue-900/40 cursor-pointer",
          p.status === 'unknown' && "border-yellow-600/60 bg-yellow-900/20 text-yellow-300 hover:bg-yellow-900/40 cursor-pointer border-dashed",
          p.status === 'internal' && "border-gray-700 bg-gray-800/30 text-gray-400 cursor-default",
        )}
      >
        <span className="truncate">{label}</span>
        {p.status === 'linked' && p.role === 'main_contact' && <Star className="w-3 h-3 text-amber-400 shrink-0" />}
        {p.status === 'linked' && p.role === 'c_level' && <Briefcase className="w-3 h-3 text-gray-400 shrink-0" />}
        {clickable && <UserPlus className="w-3 h-3 shrink-0" />}
      </button>
    );
  };

  const filteredClients = clients
    .filter(c => c.isActive !== false)
    .filter(c =>
      !companySearch ||
      c.companyName.toLowerCase().includes(companySearch.toLowerCase()) ||
      c.id.toLowerCase().includes(companySearch.toLowerCase())
    );

  return (
    <div className="space-y-1" data-testid={`email-participants-${pulseId}`}>
      {rows.map((row, rowIdx) => {
        const { inline, overflow } = splitParticipantsForDisplay(row.items);
        return (
          <div key={row.key} className="flex flex-wrap items-center gap-1.5" data-testid={`email-participants-row-${row.key}-${pulseId}`}>
            <span className="w-9 shrink-0 text-[11px] text-gray-500">{row.label}:</span>
            {inline.map(renderChip)}
            {rowIdx === 0 && direction === 'incoming' && isAdmin && data?.linked === true && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Unlink this pulse from its source email? The participants will disappear until another email is linked.')) {
                    unlinkMutation.mutate();
                  }
                }}
                disabled={unlinkMutation.isPending}
                title="Unlink source email (admin)"
                data-testid={`button-unlink-source-email-${pulseId}`}
                className="inline-flex items-center rounded-full border border-gray-700 p-0.5 text-gray-500 hover:text-red-400 hover:border-red-500/50"
              >
                <Unlink className="w-3 h-3" />
              </button>
            )}
            {overflow.length > 0 && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-0.5 rounded-full border border-gray-600 bg-gray-800/60 px-2 py-0.5 text-[11px] text-gray-300 hover:bg-gray-700"
                    data-testid={`participants-overflow-trigger-${row.key}`}
                  >
                    +{overflow.length} more
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto max-w-md bg-gray-800 border-gray-700 p-2">
                  <div className="flex flex-wrap gap-1.5">
                    {overflow.map(renderChip)}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        );
      })}

      {/* Fast-path: known contact, not on this project — 1-click add with optional role */}
      {fastAddTarget && (
        <Dialog open={!!fastAddTarget} onOpenChange={(o) => !o && setFastAddTarget(null)}>
          <DialogContent className="bg-gray-800 text-white border-gray-700 max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-base">Add to project</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-300">
              <span className="font-medium">{fastAddTarget.contactName || fastAddTarget.email}</span> is already in your contacts.
              Add them to this project?
            </p>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={fastAddRole} onValueChange={setFastAddRole}>
                <SelectTrigger className="navy-bg border-gray-600 text-white h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}{r.hint ? ` (${r.hint})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setFastAddTarget(null)}>Cancel</Button>
              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
                disabled={fastAddMutation.isPending}
                onClick={() => fastAddTarget.contactId && fastAddMutation.mutate({ contactId: fastAddTarget.contactId, role: fastAddRole })}
                data-testid="button-fast-add-confirm"
              >
                {fastAddMutation.isPending ? 'Adding…' : 'Add to project'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Full approval dialog for completely unknown addresses */}
      {approveTarget && (
        <Dialog open={!!approveTarget} onOpenChange={(o) => !o && setApproveTarget(null)}>
          <DialogContent className="bg-gray-800 text-white border-gray-700">
            <DialogHeader>
              <DialogTitle className="text-base">Approve participant: {approveTarget.email}</DialogTitle>
            </DialogHeader>

            {/* Multi-email contacts: suggest attaching this address to an existing person */}
            {!attachContact && (suggestionData?.suggestions?.length || 0) > 0 && (
              <div className="rounded border border-blue-800/60 bg-blue-950/30 p-2 space-y-1.5" data-testid="contact-suggestions">
                <p className="text-xs font-medium text-blue-300">Is this an existing contact with a new email address?</p>
                {suggestionData!.suggestions.slice(0, 3).map(s => (
                  <button
                    key={s.contactId}
                    type="button"
                    onClick={() => setAttachContact({ id: s.contactId, name: `${s.firstName} ${s.lastName}`.trim(), email: s.email || '' })}
                    data-testid={`suggestion-${s.contactId}`}
                    className="w-full text-left rounded border border-gray-700 bg-gray-900/40 px-2 py-1.5 text-xs hover:border-blue-500"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate">{s.firstName} {s.lastName}</span>
                      {s.email && <span className="text-gray-400 shrink-0 truncate max-w-[45%]">{s.email}</span>}
                    </div>
                    <div className="text-blue-300/80">{s.reasons.join(' · ')}</div>
                  </button>
                ))}
              </div>
            )}

            {/* Manual search across all contacts */}
            {!attachContact && (
              <div className="space-y-1">
                <Label className="text-xs text-gray-400">…or attach this address to another existing contact</Label>
                <Input
                  placeholder="Search contacts by name or email…"
                  value={contactSearch}
                  onChange={e => setContactSearch(e.target.value)}
                  className="navy-bg border-gray-600 text-white h-8 text-sm"
                  data-testid="input-attach-contact-search"
                />
                {contactSearch.trim().length >= 2 && (
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {allContacts
                      .filter(c => {
                        const q = contactSearch.trim().toLowerCase();
                        return `${c.firstName} ${c.lastName}`.toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
                      })
                      .slice(0, 8)
                      .map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setAttachContact({ id: c.id, name: `${c.firstName} ${c.lastName}`.trim(), email: c.email })}
                          data-testid={`attach-search-result-${c.id}`}
                          className="w-full text-left rounded border border-gray-700 bg-gray-900/40 px-2 py-1 text-xs hover:border-blue-500 flex items-center justify-between gap-2"
                        >
                          <span className="truncate">{c.firstName} {c.lastName}</span>
                          <span className="text-gray-400 truncate max-w-[50%]">{c.email}</span>
                        </button>
                      ))}
                  </div>
                )}
              </div>
            )}

            {/* Attach mode: confirm adding the address to the chosen contact */}
            {attachContact && (
              <div className="rounded border border-blue-700 bg-blue-950/40 p-2 space-y-2" data-testid="attach-confirm-box">
                <p className="text-sm text-gray-200">
                  Add <span className="font-medium text-blue-300">{approveTarget.email}</span> as an additional
                  address of <span className="font-medium">{attachContact.name}</span>
                  {attachContact.email && <span className="text-gray-400"> ({attachContact.email})</span>}?
                </p>
                <div>
                  <Label className="text-xs">Role in this project</Label>
                  <Select value={approveForm.role} onValueChange={v => setApproveForm(f => ({ ...f, role: v }))}>
                    <SelectTrigger className="navy-bg border-gray-600 text-white h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map(r => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}{r.hint ? ` (${r.hint})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <button
                  type="button"
                  onClick={() => setAttachContact(null)}
                  className="text-xs text-gray-400 hover:text-gray-200 underline"
                  data-testid="button-cancel-attach"
                >
                  No — create a new contact instead
                </button>
              </div>
            )}

            {!attachContact && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">First Name</Label>
                  <Input
                    value={approveForm.firstName}
                    onChange={e => setApproveForm(f => ({ ...f, firstName: e.target.value }))}
                    className="navy-bg border-gray-600 text-white h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Last Name</Label>
                  <Input
                    value={approveForm.lastName}
                    onChange={e => setApproveForm(f => ({ ...f, lastName: e.target.value }))}
                    className="navy-bg border-gray-600 text-white h-8 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Company</Label>
                <div className="space-y-1.5">
                  <Input
                    placeholder="Search companies…"
                    value={companySearch}
                    onChange={e => setCompanySearch(e.target.value)}
                    disabled={approveForm.noCompany}
                    className="navy-bg border-gray-600 text-white h-8 text-sm"
                  />
                  <Select
                    value={approveForm.noCompany ? '' : approveForm.clientId}
                    onValueChange={v => setApproveForm(f => ({ ...f, clientId: v, noCompany: false }))}
                    disabled={approveForm.noCompany}
                  >
                    <SelectTrigger className="navy-bg border-gray-600 text-white h-8 text-sm">
                      <SelectValue placeholder="Select company…" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredClients.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.companyName} ({c.id}){c.id === defaultClientId ? ' — project client' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={approveForm.noCompany}
                      onChange={e => setApproveForm(f => ({ ...f, noCompany: e.target.checked }))}
                    />
                    Not related to a company
                  </label>
                </div>
              </div>
              <div>
                <Label className="text-xs">Role</Label>
                <div className="space-y-1 mt-1">
                  {ROLE_OPTIONS.map(r => (
                    <label key={r.value} className="flex items-start gap-2 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name={`role-${pulseId}`}
                        checked={approveForm.role === r.value}
                        onChange={() => setApproveForm(f => ({ ...f, role: r.value }))}
                        className="mt-0.5"
                      />
                      <span>
                        {r.label}
                        {r.hint && <span className="block text-xs text-gray-400">{r.hint}</span>}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            )}
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setApproveTarget(null)}>Cancel</Button>
              {attachContact ? (
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={approveMutation.isPending}
                  onClick={() => approveMutation.mutate({
                    email: approveTarget.email,
                    role: approveForm.role,
                    attachToContactId: attachContact.id,
                  })}
                  data-testid="button-attach-confirm"
                >
                  {approveMutation.isPending ? 'Adding…' : 'Add address & link to project'}
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={
                    approveMutation.isPending ||
                    !approveForm.firstName.trim() ||
                    !approveForm.lastName.trim() ||
                    (!approveForm.noCompany && !approveForm.clientId)
                  }
                  onClick={() => approveMutation.mutate({
                    email: approveTarget.email,
                    firstName: approveForm.firstName.trim(),
                    lastName: approveForm.lastName.trim(),
                    clientId: approveForm.noCompany ? null : approveForm.clientId,
                    role: approveForm.role,
                  })}
                  data-testid="button-approve-confirm"
                >
                  {approveMutation.isPending ? 'Approving…' : 'Approve & add'}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// Task #48: admin dialog to pick the source email for an old pulse without backlink.
type LinkCandidate = {
  linkId: number;
  subject: string | null;
  fromAddress: string | null;
  fromName: string | null;
  emailDate: string;
};

function LinkSourceEmailDialog({ pulseId, projectId, open, onOpenChange }: {
  pulseId: number;
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedLinkId, setSelectedLinkId] = useState<number | null>(null);

  const { data, isLoading } = useQuery<{ candidates: LinkCandidate[]; alreadyLinked: boolean; pulseDate?: string }>({
    queryKey: ['/api/pulses', pulseId, 'email-link-candidates'],
    queryFn: async () => {
      const res = await apiRequest('GET', `/api/pulses/${pulseId}/email-link-candidates`);
      if (!res.ok) throw new Error('Failed to fetch candidates');
      return res.json();
    },
    enabled: open,
    staleTime: 0,
  });

  const linkMutation = useMutation({
    mutationFn: async (linkId: number) => {
      const res = await apiRequest('POST', `/api/pulses/${pulseId}/email-link`, { linkId });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to link email');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/pulses', pulseId, 'participants'] });
      queryClient.invalidateQueries({ queryKey: ['/api/pulses', pulseId, 'email-link-candidates'] });
      queryClient.invalidateQueries({ queryKey: ['/api/email-pulses/unlinked'] });
      toast({ title: "Source email linked — participants are now shown" });
      onOpenChange(false);
    },
    onError: (e: Error) => toast({ title: e.message, variant: "destructive" }),
  });

  const candidates = data?.candidates || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-800 text-white border-gray-700 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base">Link source email</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-gray-400">
          This pulse was created from an email before automatic linking existed.
          Pick the email it came from — participants will then appear on the pulse.
          Emails are sorted by how close their date is to the pulse.
        </p>
        <div className="max-h-72 overflow-y-auto space-y-1" data-testid={`email-link-candidates-${pulseId}`}>
          {isLoading && <p className="text-sm text-gray-400">Loading…</p>}
          {!isLoading && candidates.length === 0 && (
            <p className="text-sm text-gray-400">No unlinked emails found for project {projectId}.</p>
          )}
          {candidates.map(c => (
            <button
              key={c.linkId}
              type="button"
              onClick={() => setSelectedLinkId(c.linkId)}
              data-testid={`email-link-candidate-${c.linkId}`}
              className={cn(
                "w-full text-left rounded border px-2 py-1.5 text-xs",
                selectedLinkId === c.linkId
                  ? "border-blue-500 bg-blue-900/30"
                  : "border-gray-700 hover:border-gray-500 bg-gray-900/40",
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium truncate">{c.fromName || c.fromAddress || 'Unknown sender'}</span>
                <span className="text-gray-400 shrink-0">{format(new Date(c.emailDate), 'dd.MM.yyyy HH:mm')}</span>
              </div>
              <div className="text-gray-300 truncate">{c.subject || '(no subject)'}</div>
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
            disabled={!selectedLinkId || linkMutation.isPending}
            onClick={() => selectedLinkId && linkMutation.mutate(selectedLinkId)}
            data-testid="button-confirm-link-email"
          >
            {linkMutation.isPending ? 'Linking…' : 'Link email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
